import { Button, message, Space, Tag } from 'antd';
import axios from 'axios';
import * as Component from 'components';
import { BASE_URL, VIDEO_ASSET_API_PATH } from 'constants/api';
import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { videoService } from 'service/api/videoService';
import { TableColumnInterface } from 'type/tableType';
import { VideoType } from 'type/videoType';

export interface VideoAssetType {
  id: number;
  name: string;
  image?: string;
  source: 'inhouse' | 'mediahold' | string;
  property?: string;
  status: 'processing' | 'completed' | 'failed' | string;
  created: string;
  updated: string;
  file_size?: number;
  duration?: number;
}

interface S3Status {
  exists: boolean;
  size?: number;
  last_modified?: string;
  content_type?: string;
  etag?: string;
}

export default function Assets(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [s3StatusMap, setS3StatusMap] = useState<Record<number, S3Status>>({});
  const [loadingS3Status, setLoadingS3Status] = useState<Record<number, boolean>>({});

  const getS3KeyFromFileUrl = (fileUrl: string): string | null => {
    try {
      const url = new URL(fileUrl);
      const pathParts = url.pathname.split('/');
      const prodIndex = pathParts.indexOf('prod');

      if (prodIndex === -1) {
        return null;
      }

      const prodPath = pathParts.slice(prodIndex).join('/');
      return `s3://ucms-content-prod/${prodPath}`;
    } catch {
      return null;
    }
  };

  const fetchS3Status = async (videoId: number, s3Key: string) => {
    try {
      setLoadingS3Status(prev => ({ ...prev, [videoId]: true }));
      const response = await axios.post(`${BASE_URL}${VIDEO_ASSET_API_PATH}${videoId}/s3-status/`, {
        s3_key: s3Key,
      });
      setS3StatusMap(prev => ({ ...prev, [videoId]: response.data }));
    } catch (error: any) {
      console.error('S3 status fetch error:', error);
      setS3StatusMap(prev => ({
        ...prev,
        [videoId]: { exists: false },
      }));
    } finally {
      setLoadingS3Status(prev => ({ ...prev, [videoId]: false }));
    }
  };

  const fetchAssets = useCallback(
    async (searchParams: URLSearchParams): Promise<{ data: VideoType[]; count: number }> => {
      searchParams.set('search', searchParams.get('search') || '');

      // page_size가 없으면 기본값 10으로 설정
      if (!searchParams.get('page_size')) {
        searchParams.set('page_size', '100');
      }

      // Add filter parameters if they exist
      if (searchParams.get('ingest_source')) {
        searchParams.set('ingest_source', searchParams.get('ingest_source') || '');
      }
      if (searchParams.get('is_assigned')) {
        searchParams.set('is_assigned', searchParams.get('is_assigned') || '');
      }
      if (searchParams.get('status')) {
        searchParams.set('status', searchParams.get('status') || '');
      }

      const videos = await videoService.fetchVideos(searchParams);

      // 모든 video의 S3 status를 한 번에 fetch (완료될 때까지 대기)
      const videosToCheck = videos.data.filter(video => video.file_url && !s3StatusMap[video.id]);

      if (videosToCheck.length > 0) {
        // S3 status 확인이 완료될 때까지 대기
        const results = await Promise.all(
          videosToCheck.map(async video => {
            const s3Key = getS3KeyFromFileUrl(video.file_url!);
            if (!s3Key) return null;

            try {
              const response = await axios.post(
                `${BASE_URL}${VIDEO_ASSET_API_PATH}${video.id}/s3-status/`,
                { s3_key: s3Key }
              );
              return { videoId: video.id, status: response.data };
            } catch (error: any) {
              console.error(`S3 status fetch error for video ${video.id}:`, error);
              return { videoId: video.id, status: { exists: false } };
            }
          })
        );

        // 모든 S3 status를 한 번에 업데이트
        const newS3StatusMap: Record<number, S3Status> = {};
        results.forEach(result => {
          if (result) {
            newS3StatusMap[result.videoId] = result.status;
          }
        });
        setS3StatusMap(prev => ({ ...prev, ...newS3StatusMap }));
      }

      return {
        data: videos.data,
        count: videos.count,
      };
    },
    []
  );

  const handleDownload = async (record: VideoType) => {
    try {
      if (!record.file_url) {
        message.error('File URL이 없습니다.');
        return;
      }

      const fileUrl = new URL(record.file_url);
      const pathParts = fileUrl.pathname.split('/');
      const prodIndex = pathParts.indexOf('prod');

      if (prodIndex === -1) {
        message.error('URL에서 prod 경로를 찾을 수 없습니다.');
        return;
      }

      const prodPath = pathParts.slice(prodIndex).join('/');
      const s3Key = `s3://ucms-content-prod/${prodPath}`;

      message.loading({ content: 'Presigned URL 생성 중...', key: 'download', duration: 0 });

      const response = await axios.post(
        `${BASE_URL}${VIDEO_ASSET_API_PATH}${record.id}/download-presigned-url/`,
        { s3_key: s3Key }
      );

      const presignedUrl = response.data.presigned_url || response.data.url;

      if (!presignedUrl) {
        message.error({ content: 'Presigned URL을 받을 수 없습니다.', key: 'download' });
        return;
      }

      message.success({ content: '다운로드를 시작합니다...', key: 'download' });

      // ✅ 브라우저 기본 다운로드로 넘기기
      const a = document.createElement('a');
      a.href = presignedUrl;

      // download 속성은 "같은 오리진"이 아니면 무시되는 경우가 많아서
      // 있어도 되고 없어도 됩니다. (파일명은 보통 서버의 Content-Disposition에 좌우됨)
      // a.download = prodPath.split('/').pop() || record.file_name || 'download';

      a.target = '_blank'; // 새 탭에서 처리(브라우저 다운로드 매니저가 동작)
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('Download error:', error);
      message.error({
        content: `다운로드 실패: ${error.message || '알 수 없는 오류'}`,
        key: 'download',
      });
    }
  };

  const columns: TableColumnInterface<VideoType>[] = [
    {
      title: 'File Name',
      dataIndex: 'file_name',
      key: 'file_name',
      width: '60%',
      sorter: true as any,
      render: (_: string, record: VideoType) => {
        const getFileNameFromUrl = (url: string): string => {
          try {
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/');
            return pathParts[pathParts.length - 1] || url;
          } catch {
            // URL 파싱 실패 시 경로에서 직접 추출
            const pathParts = url.split('/');
            return pathParts[pathParts.length - 1] || url;
          }
        };

        const fileName = record.file_url
          ? getFileNameFromUrl(record.file_url)
          : record.file_name || '-';

        return (
          <div style={{ maxWidth: '600px', wordBreak: 'break-word' }}>
            <div style={{ color: 'black', fontSize: '12px', marginTop: '2px' }}>{fileName}</div>
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      render: (_: string, record: VideoType) => {
        const s3Status = s3StatusMap[record.id];
        const isLoading = loadingS3Status[record.id];

        if (isLoading) {
          return <Tag color="processing">Checking...</Tag>;
        }

        if (!s3Status) {
          if (record.file_url) {
            const s3Key = getS3KeyFromFileUrl(record.file_url);
            if (s3Key) {
              return (
                <Button size="small" type="link" onClick={() => fetchS3Status(record.id, s3Key)}>
                  Check S3
                </Button>
              );
            }
          }
          return <Tag>-</Tag>;
        }

        return (
          <Space direction="vertical" size="small">
            <Tag color={s3Status.exists ? 'green' : 'red'}>
              {s3Status.exists ? 'Uploaded' : 'Failed'}
            </Tag>
            {s3Status.exists && s3Status.size && (
              <div style={{ fontSize: '11px', color: '#666' }}>
                Size: {(s3Status.size / (1024 * 1024 * 1024)).toFixed(2)} GB
              </div>
            )}
            {s3Status.exists && s3Status.last_modified && (
              <div style={{ fontSize: '11px', color: '#666' }}>
                Modified: {new Date(s3Status.last_modified).toLocaleDateString()}
              </div>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Download',
      dataIndex: 'download',
      key: 'download',
      width: '10%',
      render: (_: string, record: VideoType) => (
        <Button type="primary" onClick={() => handleDownload(record)}>
          Download
        </Button>
      ),
    },
  ];

  return (
    <Component.BasePageComponent>
      <Component.TableComponent<VideoType>
        name="Video Assets"
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        dataFetch={fetchAssets}
        columns={columns}
      />
    </Component.BasePageComponent>
  );
}
